(() => {
  'use strict';

  if (window.PaulissenCollectionAvailabilityReady) return;
  window.PaulissenCollectionAvailabilityReady = true;

  let activeController;
  let priceTimer;
  let observerFrame;

  const AVAILABILITY_PARAM = 'filter.v.availability';
  const IN_STOCK_VALUE = '1';
  const AVAILABILITY_MODE_PARAM = 'pw_availability';
  const SHOW_ALL_VALUE = 'all';
  const SHOW_STOCK_VALUE = 'stock';
  const CUSTOM_SECTION_SELECTOR = '[data-results-section]';
  const NATIVE_TOOLBAR_SELECTOR = 'products-toolbar#products-toolbar';
  const MOBILE_FILTER_MEDIA = window.matchMedia('(max-width: 760px)');

  const getCustomSection = () => document.querySelector(CUSTOM_SECTION_SELECTOR);

  const hasActiveFacetValue = (facet) => {
    if (facet.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked')) return true;
    return [...facet.querySelectorAll('input[type="number"]')]
      .some((input) => input.value.trim() !== '');
  };

  const configureResponsiveFacets = (section, force = false) => {
    if (!(section instanceof Element)) return;
    const panel = section.querySelector('.pw-facets__panel');
    if (!panel) return;

    const mode = MOBILE_FILTER_MEDIA.matches ? 'mobile' : 'desktop';
    if (!force && panel.dataset.pwResponsiveMode === mode) return;
    panel.dataset.pwResponsiveMode = mode;

    if (MOBILE_FILTER_MEDIA.matches) {
      panel.open = false;
      section.querySelectorAll('.pw-facet').forEach((facet) => {
        facet.open = hasActiveFacetValue(facet);
      });
      return;
    }

    panel.open = true;
    section.querySelectorAll('.pw-facet').forEach((facet, index) => {
      facet.open = hasActiveFacetValue(facet) || index < 3;
    });
  };

  const getAvailabilityMode = (target = window.location.href) => {
    const url = new URL(target, window.location.origin);
    return url.searchParams.get(AVAILABILITY_MODE_PARAM) === SHOW_ALL_VALUE
      ? SHOW_ALL_VALUE
      : SHOW_STOCK_VALUE;
  };

  const normalizeAvailability = (target, { preserveCurrentMode = true } = {}) => {
    const url = new URL(target, window.location.origin);
    const requestedMode = url.searchParams.get(AVAILABILITY_MODE_PARAM);
    let mode = SHOW_STOCK_VALUE;

    if (requestedMode === SHOW_ALL_VALUE || requestedMode === SHOW_STOCK_VALUE) {
      mode = requestedMode;
    } else if (preserveCurrentMode) {
      mode = getAvailabilityMode();
    }

    if (mode === SHOW_ALL_VALUE) {
      url.searchParams.delete(AVAILABILITY_PARAM);
      url.searchParams.set(AVAILABILITY_MODE_PARAM, SHOW_ALL_VALUE);
    } else {
      url.searchParams.set(AVAILABILITY_PARAM, IN_STOCK_VALUE);
      url.searchParams.delete(AVAILABILITY_MODE_PARAM);
    }

    return url;
  };

  const hasExplicitAvailabilityMode = (url = new URL(window.location.href)) => (
    url.searchParams.get(AVAILABILITY_MODE_PARAM) === SHOW_ALL_VALUE ||
    url.searchParams.get(AVAILABILITY_PARAM) === IN_STOCK_VALUE
  );

  const createSwitch = (stockOnly, onClick, modifier = '') => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `pw-availability-toggle${modifier ? ` ${modifier}` : ''}`;
    button.dataset.availabilityToggle = '';
    button.setAttribute('role', 'switch');
    button.setAttribute('aria-checked', stockOnly ? 'true' : 'false');
    button.setAttribute('aria-label', `Alleen leverbare producten tonen: ${stockOnly ? 'ingeschakeld' : 'uitgeschakeld'}`);
    button.innerHTML = `
      <span class="pw-availability-toggle__label">Alleen leverbare producten tonen</span>
      <span class="pw-availability-toggle__track" aria-hidden="true">
        <span class="pw-availability-toggle__thumb"></span>
      </span>
    `;
    button.addEventListener('click', onClick);
    return button;
  };

  const buildCustomFormUrl = (form) => {
    const actionUrl = new URL(form.action, window.location.origin);
    const currentUrl = new URL(window.location.href);
    const url = new URL(actionUrl);

    if (currentUrl.pathname === actionUrl.pathname || currentUrl.pathname.startsWith(`${actionUrl.pathname}/`)) {
      url.pathname = currentUrl.pathname;
    }

    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
      if (String(value).trim() !== '') url.searchParams.append(key, value);
    }

    if (getAvailabilityMode() === SHOW_ALL_VALUE) {
      url.searchParams.set(AVAILABILITY_MODE_PARAM, SHOW_ALL_VALUE);
    }

    return normalizeAvailability(url);
  };

  const hideCustomRedundantFacets = (section) => {
    section.querySelectorAll('.pw-facet').forEach((facet) => {
      const options = Array.from(facet.querySelectorAll('input[type="checkbox"]'));
      if (options.length === 0) return;
      const meaningfulOptions = options.filter((option) => option.checked || !option.disabled);
      facet.hidden = meaningfulOptions.length <= 1;
    });

    const facets = section.querySelector('.pw-facets');
    const layout = section.querySelector('.pw-results-layout');
    if (!facets) return;

    const hasVisibleFacet = Array.from(facets.querySelectorAll('.pw-facet')).some((facet) => !facet.hidden);
    facets.hidden = !hasVisibleFacet;
    layout?.classList.toggle('pw-results-layout--no-filters', !hasVisibleFacet);
  };

  const hideNativeRedundantFacets = () => {
    document.querySelectorAll('#facet-filters .facets__filters > details-disclosure').forEach((wrapper) => {
      const details = wrapper.querySelector('details.filter');
      if (!details || details.id === 'filter-sort') return;

      const options = Array.from(details.querySelectorAll('input[type="checkbox"], input[type="radio"]'))
        .filter((option) => option.name !== 'sort_by');
      if (options.length === 0) return;

      const meaningfulOptions = options.filter((option) => option.checked || !option.disabled);
      wrapper.hidden = meaningfulOptions.length <= 1;
    });
  };

  const syncNativeFacetForm = () => {
    const form = document.querySelector('#facets');
    if (!form) return;

    form.querySelectorAll('[data-pw-availability-field]').forEach((field) => field.remove());

    const mode = getAvailabilityMode();
    const field = document.createElement('input');
    field.type = 'hidden';
    field.dataset.pwAvailabilityField = '';

    if (mode === SHOW_ALL_VALUE) {
      field.name = AVAILABILITY_MODE_PARAM;
      field.value = SHOW_ALL_VALUE;
    } else {
      field.name = AVAILABILITY_PARAM;
      field.value = IN_STOCK_VALUE;
    }

    form.append(field);
  };

  const renderCustomSection = async (target, { updateHistory = true, focusResults = false } = {}) => {
    const currentSection = getCustomSection();
    if (!currentSection) {
      window.location.assign(normalizeAvailability(target));
      return;
    }

    activeController?.abort();
    activeController = new AbortController();

    const cleanUrl = normalizeAvailability(target);
    cleanUrl.searchParams.delete('section_id');

    const requestUrl = new URL(cleanUrl);
    requestUrl.searchParams.set('section_id', currentSection.dataset.sectionId);

    currentSection.classList.add('is-loading');
    currentSection.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(requestUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: activeController.signal
      });
      if (!response.ok) throw new Error(`Filterweergave kon niet worden geladen (${response.status}).`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextSection = parsed.querySelector(CUSTOM_SECTION_SELECTOR);
      if (!nextSection) throw new Error('Shopify gaf geen geldige resultatensectie terug.');

      currentSection.replaceWith(nextSection);
      if (updateHistory) window.history.pushState({}, '', cleanUrl);
      initializeCustomSection(nextSection);

      if (focusResults) nextSection.querySelector('[data-results-grid]')?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn(error);
        window.location.assign(cleanUrl);
      }
    }
  };

  const createCustomToggle = (section) => {
    const toolbar = section.querySelector('.pw-results-toolbar');
    if (!toolbar || toolbar.querySelector('[data-availability-toggle]')) return;

    const stockOnly = getAvailabilityMode() === SHOW_STOCK_VALUE;
    const toggle = createSwitch(stockOnly, () => {
      const targetUrl = new URL(window.location.href);
      targetUrl.searchParams.delete('page');
      targetUrl.searchParams.set(
        AVAILABILITY_MODE_PARAM,
        stockOnly ? SHOW_ALL_VALUE : SHOW_STOCK_VALUE
      );
      renderCustomSection(targetUrl, { focusResults: true });
    });

    const count = toolbar.querySelector('.pw-results-toolbar__count');
    if (count) count.after(toggle);
    else toolbar.prepend(toggle);
  };

  const toggleTagFilter = (input) => {
    const tag = String(input.dataset.pwTagFilter || '').trim().toLowerCase();
    const basePath = String(input.dataset.pwTagBasePath || '').replace(/\/$/, '');
    if (!tag || !basePath) return;

    const targetUrl = new URL(window.location.href);
    targetUrl.searchParams.delete('page');
    targetUrl.pathname = input.checked
      ? `${basePath}/${encodeURIComponent(tag)}`
      : basePath;
    renderCustomSection(targetUrl, { focusResults: true });
  };

  const initializeCustomSection = (section = getCustomSection()) => {
    if (!section) return;
    configureResponsiveFacets(section);
    if (section.dataset.filtersReady === 'true') return;
    section.dataset.filtersReady = 'true';

    hideCustomRedundantFacets(section);
    createCustomToggle(section);

    const form = section.querySelector('[data-facets-form]');
    if (!form) return;

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      renderCustomSection(buildCustomFormUrl(form), { focusResults: true });
    });

    form.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

      if (target.matches('[data-pw-tag-filter]')) {
        toggleTagFilter(target);
        return;
      }

      if (target.type === 'number') {
        window.clearTimeout(priceTimer);
        priceTimer = window.setTimeout(() => form.requestSubmit(), 450);
        return;
      }

      form.requestSubmit();
    });

    section.querySelectorAll('[data-facet-link], [data-pagination] a').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        renderCustomSection(link.href, { focusResults: link.closest('[data-pagination]') !== null });
      });
    });
  };

  const createNativeToggle = (toolbar) => {
    if (!toolbar || toolbar.querySelector('[data-availability-toggle]')) return;

    const stockOnly = getAvailabilityMode() === SHOW_STOCK_VALUE;
    const toggle = createSwitch(stockOnly, () => {
      const targetUrl = new URL(window.location.href);
      targetUrl.searchParams.delete('page');
      targetUrl.searchParams.set(
        AVAILABILITY_MODE_PARAM,
        stockOnly ? SHOW_ALL_VALUE : SHOW_STOCK_VALUE
      );
      window.location.assign(normalizeAvailability(targetUrl));
    }, 'pw-availability-toggle--native');

    const layoutControl = toolbar.querySelector('.products-toolbar__layout');
    const compareControl = toolbar.querySelector('.products-toolbar__compare');

    if (layoutControl) toolbar.insertBefore(toggle, layoutControl);
    else if (compareControl) toolbar.insertBefore(toggle, compareControl);
    else toolbar.append(toggle);
  };

  const initializeNativeToolbar = () => {
    const toolbar = document.querySelector(NATIVE_TOOLBAR_SELECTOR);
    if (!toolbar) return;
    syncNativeFacetForm();
    hideNativeRedundantFacets();
    createNativeToggle(toolbar);
  };

  const ensureInitialAvailability = () => {
    const currentUrl = new URL(window.location.href);
    if (hasExplicitAvailabilityMode(currentUrl)) return false;

    const normalizedUrl = normalizeAvailability(currentUrl, { preserveCurrentMode: false });
    normalizedUrl.searchParams.delete('page');

    if (getCustomSection()) {
      window.history.replaceState({}, '', normalizedUrl);
      renderCustomSection(normalizedUrl, { updateHistory: false });
    } else {
      window.location.replace(normalizedUrl);
    }

    return true;
  };

  const initializeAll = () => {
    initializeCustomSection();
    initializeNativeToolbar();
  };

  const scheduleInitializeAll = () => {
    if (observerFrame) return;
    observerFrame = window.requestAnimationFrame(() => {
      observerFrame = null;
      initializeAll();
      syncNativeFacetForm();
      hideNativeRedundantFacets();
    });
  };

  const start = () => {
    if (ensureInitialAvailability()) return;

    initializeAll();

    const observedRoot = document.querySelector('main') || document.body;
    new MutationObserver(scheduleInitializeAll).observe(observedRoot, { childList: true, subtree: true });

    const handleMediaChange = () => {
      const section = getCustomSection();
      if (section) configureResponsiveFacets(section, true);
    };
    if (typeof MOBILE_FILTER_MEDIA.addEventListener === 'function') {
      MOBILE_FILTER_MEDIA.addEventListener('change', handleMediaChange);
    } else {
      MOBILE_FILTER_MEDIA.addListener(handleMediaChange);
    }
  };

  window.addEventListener('popstate', () => {
    if (getCustomSection()) renderCustomSection(window.location.href, { updateHistory: false });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
